
import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import UserProfilePost from "./UserProfilePost";
import UserProfilePostSkeleton from "./UserProfilePostSkeleton";
import CreatePostForm from "./CreatePostForm";

interface Post {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  has_liked?: boolean;
  media?: {
    id: string;
    media_url: string;
    media_type: string;
  }[];
}

interface UserProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

const UserFeed = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<Record<string, UserProfile>>({});
  
  const loadPosts = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get user follows to fetch posts from followed users and own posts
      const { data: followingData } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user.id);
        
      const followingIds = followingData?.map(f => f.following_id) || [];
      // Include user's own ID to see their posts too
      const userIds = [...followingIds, user.id];
      
      // Fetch posts from followed users and user's own posts
      const { data: postsData, error: postsError } = await supabase
        .from('network_posts')
        .select('*')
        .in('user_id', userIds)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (postsError) throw postsError;
      
      if (!postsData || postsData.length === 0) {
        setLoading(false);
        setPosts([]);
        return;
      }
      
      // Get profiles for all post authors
      const uniqueUserIds = [...new Set(postsData.map(post => post.user_id))];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url')
        .in('id', uniqueUserIds);
      
      // Create a map of user_id to profile
      const profilesMap: Record<string, UserProfile> = {};
      profilesData?.forEach(profile => {
        profilesMap[profile.id] = profile;
      });
      
      setProfiles(profilesMap);
      
      // Get enhanced posts with likes, comments count and media
      const enhancedPosts = await Promise.all(postsData.map(async (post) => {
        // Get likes count
        const { count: likesCount } = await supabase
          .from('post_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', post.id)
          .eq('type', 'like');
          
        // Get comments count
        const { count: commentsCount } = await supabase
          .from('post_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', post.id)
          .eq('type', 'comment');
          
        // Check if current user has liked
        const { data: likeData } = await supabase
          .from('post_interactions')
          .select('id')
          .eq('post_id', post.id)
          .eq('user_id', user.id)
          .eq('type', 'like')
          .maybeSingle();
          
        // Get post media
        const { data: mediaData } = await supabase
          .from('post_media')
          .select('*')
          .eq('post_id', post.id);
          
        return {
          ...post,
          likes_count: likesCount || 0,
          comments_count: commentsCount || 0,
          has_liked: !!likeData,
          media: mediaData || []
        };
      }));
      
      setPosts(enhancedPosts);
    } catch (error) {
      console.error("Error loading posts:", error);
      toast({
        title: "Error",
        description: "Failed to load posts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [user]);

  const handleLikePost = async (postId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to like posts",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const post = posts.find(p => p.id === postId);
      const isLiked = post?.has_liked;
      
      // Optimistically update UI
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId
            ? { 
                ...post, 
                has_liked: !isLiked, 
                likes_count: isLiked ? post.likes_count - 1 : post.likes_count + 1 
              }
            : post
        )
      );
      
      if (isLiked) {
        // Remove like
        await supabase
          .from('post_interactions')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id)
          .eq('type', 'like');
      } else {
        // Add like
        await supabase
          .from('post_interactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            type: 'like'
          });
      }
    } catch (error) {
      console.error("Error liking/unliking post:", error);
      toast({
        title: "Error",
        description: "Failed to update like status",
        variant: "destructive"
      });
      // Revert optimistic update on error
      loadPosts();
    }
  };

  const handleComment = async (postId: string, commentText: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to comment on posts",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Add comment
      await supabase
        .from('post_interactions')
        .insert({
          post_id: postId,
          user_id: user.id,
          type: 'comment',
          comment_text: commentText
        });
      
      // Update post in state
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, comments_count: post.comments_count + 1 }
            : post
        )
      );
      
      toast({
        title: "Comment added",
        description: "Your comment was added successfully"
      });
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <CreatePostForm onPostCreated={loadPosts} />
      
      {loading ? (
        <>
          <UserProfilePostSkeleton />
          <UserProfilePostSkeleton />
        </>
      ) : posts.length > 0 ? (
        posts.map((post) => (
          <UserProfilePost
            key={post.id}
            post={post}
            profile={profiles[post.user_id]}
            onLike={() => handleLikePost(post.id)}
            onComment={(comment) => handleComment(post.id, comment)}
          />
        ))
      ) : (
        <div className="text-center py-8 bg-muted/20 rounded-lg border border-dashed">
          <p className="text-muted-foreground">No posts yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first post or follow other users to see their posts</p>
        </div>
      )}
    </div>
  );
};

export default UserFeed;
